import React, { useState, useEffect, useMemo } from 'react';
import { Form, Spinner, Card, ListGroup } from 'react-bootstrap';
import dbService from '../../../services/dbService';
import PageContainer from '../../layout/PageContainer';
import GraphNode from './GraphNode';
import GraphEdge from './GraphEdge';
import './LinkAnalysis.css';
import { useParams, useNavigate } from 'react-router-dom';
import { PersonFill, Building, TelephoneFill } from 'react-bootstrap-icons';

const LinkAnalysis = () => {
    const [allData, setAllData] = useState({ customers: [], transactions: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [selectedNode, setSelectedNode] = useState(null);
    const { customerId } = useParams();
    const navigate = useNavigate();

    // This useEffect ONLY fetches data. It has no permission checks.
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const [customers, transactions] = await Promise.all([
                dbService.getAll('customers'), 
                dbService.getAll('transactions')
            ]);
            setAllData({ customers, transactions });

            if (customerId) {
                const initialNode = customers.find(c => c.id === customerId);
                setSelectedNode(initialNode);
            }
            setIsLoading(false);
        };
        fetchData();
    }, [customerId]);

    // This useMemo hook calculates the graph layout based on the selected customer.
    const graphData = useMemo(() => {
        if (!selectedNode || allData.customers.length === 0) return { nodes: [], edges: [] };

        const nodes = new Map();
        const edges = [];
        const viewport = { width: 800, height: 600 };

        // 1. Add the central node
        nodes.set(selectedNode.id, { ...selectedNode, type: 'customer', x: viewport.width / 2, y: viewport.height / 2, icon: <PersonFill size={20}/>, label: selectedNode.fullName });

        // 2. Find direct counterparties from transactions
        const directTxns = allData.transactions.filter(t => t.customerId === selectedNode.id);
        const counterparties = new Map();
        directTxns.forEach(txn => {
            const cpName = txn.counterpartyName || 'Unknown';
            if (!counterparties.has(cpName)) {
                counterparties.set(cpName, { id: `cp-${cpName}`, label: cpName, type: 'counterparty', icon: <Building size={20}/>, txnCount: 0, totalAmount: 0 });
            }
            const cp = counterparties.get(cpName);
            cp.txnCount++;
            cp.totalAmount += txn.amount;
        });
        
        // 3. Find other customers linked to these counterparties (second-degree connections)
        counterparties.forEach(cp => {
            const otherTxns = allData.transactions.filter(t => (t.counterpartyName || 'Unknown') === cp.label && t.customerId !== selectedNode.id);
            const otherCustomers = [...new Set(otherTxns.map(t => t.customerId))];
            otherCustomers.forEach(otherCustId => {
                const otherCustomer = allData.customers.find(c => c.id === otherCustId);
                if (otherCustomer && !nodes.has(otherCustId)) {
                    nodes.set(otherCustId, { ...otherCustomer, type: 'customer', icon: <PersonFill size={20}/>, label: otherCustomer.fullName });
                    edges.push({ from: otherCustId, to: cp.id, label: 'Shared Counterparty' });
                }
            });
        });

        // 4. Find links via shared identifiers (e.g., phone numbers)
        const centralPhone = selectedNode.sharedIdentifiers?.phone;
        if (centralPhone) {
            const sharedIdNodeId = `shared-${centralPhone}`;
            if (!nodes.has(sharedIdNodeId)) {
                nodes.set(sharedIdNodeId, { id: sharedIdNodeId, label: `Phone: ${centralPhone}`, type: 'shared-identifier', icon: <TelephoneFill /> });
            }
            edges.push({ from: selectedNode.id, to: sharedIdNodeId, label: 'Shared ID' });

            allData.customers.forEach(customer => {
                if (customer.id !== selectedNode.id && customer.sharedIdentifiers?.phone === centralPhone) {
                    if (!nodes.has(customer.id)) {
                         nodes.set(customer.id, { ...customer, type: 'customer', icon: <PersonFill size={20}/>, label: customer.fullName });
                    }
                    edges.push({ from: customer.id, to: sharedIdNodeId, label: 'Shared ID' });
                }
            });
        }
        
        [...counterparties.values()].forEach(cp => nodes.set(cp.id, cp));
        directTxns.forEach(txn => {
            const cpName = txn.counterpartyName || 'Unknown';
            edges.push({ from: selectedNode.id, to: `cp-${cpName}`, label: `$${txn.amount?.toLocaleString()}` });
        });

        // Simple circular layout algorithm
        const nodeList = [...nodes.values()];
        const central = nodeList.find(n => n.id === selectedNode.id);
        const others = nodeList.filter(n => n.id !== selectedNode.id);
        const angleStep = (2 * Math.PI) / (others.length || 1);
        const radius = Math.min(viewport.width, viewport.height) / 3;
        others.forEach((node, i) => {
            node.x = (viewport.width / 2) + radius * Math.cos(i * angleStep);
            node.y = (viewport.height / 2) + radius * Math.sin(i * angleStep);
        });
        
        const finalNodes = central ? [central, ...others] : others;

        return { nodes: finalNodes, edges };
    }, [selectedNode, allData]);

    const handleNodeClick = (node) => {
        if (node.type === 'customer' && node.id !== selectedNode?.id) {
            navigate(`/link-analysis/${node.id}`);
        } else {
            setSelectedNode(node);
        }
    };

    if (isLoading) {
        return <PageContainer title="Interactive Link Analysis"><div className="text-center p-5"><Spinner animation="border" /></div></PageContainer>;
    }

    return (
        <PageContainer title="Interactive Link Analysis Dashboard">
            <div className="link-analysis-container">
                <div className="graph-panel">
                    <Card>
                        <Card.Body>
                            <Form.Group>
                                <Form.Label><strong>Select a Central Customer to Analyze</strong></Form.Label>
                                <Form.Select value={selectedNode?.id || ''} onChange={e => e.target.value ? navigate(`/link-analysis/${e.target.value}`) : navigate('/link-analysis')}>
                                    <option value="">Select a customer...</option>
                                    {allData.customers.map(c => <option key={c.id} value={c.id}>{c.fullName}</option>)}
                                </Form.Select>
                            </Form.Group>
                            
                            <div className="graph-viewport">
                                {!selectedNode && <div className="graph-placeholder">Please select a customer to visualize their network.</div>}
                                {selectedNode && (
                                    <>
                                        {graphData.edges.map((edge, i) => <GraphEdge key={i} edge={edge} nodes={graphData.nodes} />)}
                                        {graphData.nodes.map(node => <GraphNode key={node.id} node={node} isSelected={selectedNode?.id === node.id} onClick={handleNodeClick} />)}
                                    </>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="details-panel">
                    <Card>
                        <Card.Header as="h5">Selected Node Details</Card.Header>
                        {selectedNode ? (
                            <ListGroup variant="flush">
                                <ListGroup.Item><strong>Name:</strong> {selectedNode.label}</ListGroup.Item>
                                
                                {/* --- THIS IS THE CRITICAL FIX --- */}
                                {/* Use optional chaining (?.) to prevent the crash */}
                                <ListGroup.Item><strong>Type:</strong> <span className="text-capitalize">{selectedNode.type?.replace('-', ' ') || 'N/A'}</span></ListGroup.Item>
                                {/* --- END OF CRITICAL FIX --- */}

                                {selectedNode.type === 'customer' && <ListGroup.Item><strong>Risk Score:</strong> {selectedNode.riskBreakdown?.total}</ListGroup.Item>}
                                {selectedNode.type === 'counterparty' && <ListGroup.Item><strong>Total Transactions (with focus):</strong> {selectedNode.txnCount}</ListGroup.Item>}
                                {selectedNode.type === 'counterparty' && <ListGroup.Item><strong>Total Volume (with focus):</strong> ${selectedNode.totalAmount?.toLocaleString()}</ListGroup.Item>}
                            </ListGroup>
                        ) : (
                            <Card.Body><p className="text-muted">Click a node or select one from the dropdown to begin analysis.</p></Card.Body>
                        )}
                    </Card>
                </div>
            </div>
        </PageContainer>
    );
};

export default LinkAnalysis;